/**
 * Mobile Upload slice - tracks mobile upload state for UI purposes
 * 
 * The actual upload uses the existing background upload infrastructure:
 * - handleVideoUpload/handleImageUpload → processVideoFile/processImageFile → queueUpload
 * - processQueuedUploads → startUpload → createPlaceholderSection → upload to S3 → completeSection
 * 
 * This slice only tracks:
 * - Whether we're in mobile upload mode (file selected, awaiting title)
 * - Meeting ID once created (for redirect tracking)
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

/**
 * Status enum for mobile upload flow
 */
export const MOBILE_UPLOAD_STATUS = {
  IDLE: 'idle',
  FILE_SELECTED: 'file-selected', // File picked, awaiting title
  UPLOADING: 'uploading', // Meeting created, upload in progress
  COMPLETED: 'completed',
};

const initialState = {
  // File metadata for UI display
  fileName: null,
  fileType: null, // 'video' | 'image'
  
  // Meeting info
  meetingId: null,
  
  // Status tracking
  status: MOBILE_UPLOAD_STATUS.IDLE,
};

const mobileUploadSlice = createSlice({
  name: 'mobileUpload',
  initialState,
  reducers: {
    // Set file info when user selects a file (before title modal)
    setMobileFileSelected: (state, action) => {
      const { fileName, fileType } = action.payload;
      state.fileName = fileName;
      state.fileType = fileType;
      state.status = MOBILE_UPLOAD_STATUS.FILE_SELECTED;
    },

    // Set meeting ID after creation and mark as uploading
    setMobileUploading: (state, action) => {
      state.meetingId = action.payload;
      state.status = MOBILE_UPLOAD_STATUS.UPLOADING;
    },

    // Mark as completed (triggers redirect)
    setMobileUploadCompleted: (state) => {
      state.status = MOBILE_UPLOAD_STATUS.COMPLETED;
    },

    // Reset all mobile upload state
    resetMobileUpload: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setMobileFileSelected,
  setMobileUploading,
  setMobileUploadCompleted,
  resetMobileUpload,
} = mobileUploadSlice.actions;

// Selectors
export const selectMobileUploadState = (state) => state.mobileUpload;
export const selectMobileUploadStatus = (state) => state.mobileUpload.status;
export const selectMobileMeetingId = (state) => state.mobileUpload.meetingId;
export const selectIsMobileFileSelected = (state) => {
  return state.mobileUpload.status === MOBILE_UPLOAD_STATUS.FILE_SELECTED;
};
export const selectIsMobileUploading = (state) => {
  return state.mobileUpload.status === MOBILE_UPLOAD_STATUS.UPLOADING;
};
export const selectIsMobileUploadComplete = (state) => {
  return state.mobileUpload.status === MOBILE_UPLOAD_STATUS.COMPLETED;
};

export default mobileUploadSlice.reducer;
